import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { Currency, IssueContactCreditInput } from "@/gql/graphql";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { issueContactCreditMutation } from "@/app/ContactCredits/GraphQL/contactCreditMutations";
import { useFormikContext } from "formik";

type IssueContactCreditDialogProps = {
  contactId: string;
  currency?: Currency;
};

export function IssueContactCreditDialog({
  contactId,
  currency = Currency.Aud,
}: IssueContactCreditDialogProps) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(issueContactCreditMutation);

  const validation = z.object({
    amount: z
      .number({ error: "Enter an amount" })
      .min(1, { error: "Amount must be greater than 0" }),
    currency: z.enum(Currency),
    description: z.string().nullable().optional(),
  });

  return (
    <BaseDialog subtitle="Credit is held against the contact and comes off the next invoice raised in the same currency">
      <GenericForm<IssueContactCreditInput>
        validationSchema={toFormikValidationSchema(validation)}
        initialValues={{
          contactId,
          amount: null!,
          currency,
          description: "",
        }}
        onSubmit={async (values) => {
          await mutateAsync({ input: values });

          return close();
        }}
        className="grid grid-cols-3 gap-6 sm:grid-cols-12"
      >
        <FormContent />
      </GenericForm>
    </BaseDialog>
  );
}

function FormContent() {
  const { values } = useFormikContext<IssueContactCreditInput>();

  return (
    <>
      <CurrencyTypeInput
        name="currency"
        label="Currency"
        className="col-span-4"
      />
      <CurrencyInput
        name="amount"
        label="Amount"
        currency={values.currency}
        className="col-span-8"
        autoFocus
      />
      <TextInput
        name="description"
        label="Reason"
        className="col-span-12"
        optionalLabel
      />
      <DialogSubmitButton text="Issue credit" />
    </>
  );
}
